<template>
  <div>
    <!-- ===== Tour overlay + tooltip ===== -->
    <template v-if="show">
      <!-- 4-panel spotlight surrounding the highlighted element -->
      <template v-if="targetRect">
        <div class="ob-panel" :style="panels.top"></div>
        <div class="ob-panel" :style="panels.bottom"></div>
        <div class="ob-panel" :style="panels.left"></div>
        <div class="ob-panel" :style="panels.right"></div>
        <div class="ob-ring" :style="ringStyle"></div>
      </template>
      <div v-else class="ob-overlay-solid"></div>

      <!-- Tooltip bubble -->
      <div class="ob-tooltip" :style="[tooltipStyle, { fontSize: fontSizes[fontLevel] }]">
        <div class="ob-header">
          <span class="ob-step-counter">{{ stepIndex + 1 }} / {{ steps.length }}</span>
          <div class="ob-font-ctrl">
            <button
              class="ob-font-btn"
              :disabled="fontLevel === 0"
              title="Decrease text size"
              @click="changeFont(-1)"
            ><span class="ob-a-small">A</span>−</button>
            <button
              class="ob-font-btn"
              :disabled="fontLevel === fontSizes.length - 1"
              title="Increase text size"
              @click="changeFont(1)"
            >+<span class="ob-a-large">A</span></button>
          </div>
        </div>
        <h6 class="ob-title">{{ currentStep.title }}</h6>
        <p class="ob-text">{{ currentStep.text }}</p>
        <div class="ob-actions">
          <button class="btn btn-sm ob-dim-btn" @click="skip">Skip tour</button>
          <button class="btn btn-sm btn-primary ml-2" @click="next">
            {{ stepIndex === steps.length - 1 ? 'Done ✓' : 'Next →' }}
          </button>
        </div>
      </div>
    </template>

    <!-- ===== Restart modal (opened via $root.$emit('restart-tour') from NavBar) ===== -->
    <template v-if="showRestartModal">
      <div class="ob-restart-overlay" @click.self="showRestartModal = false"></div>
      <div class="ob-restart-card" :style="{ fontSize: fontSizes[fontLevel] }">
        <div class="ob-header">
          <span class="ob-restart-label">Tutorial</span>
          <div class="ob-font-ctrl">
            <button
              class="ob-font-btn"
              :disabled="fontLevel === 0"
              title="Decrease text size"
              @click="changeFont(-1)"
            ><span class="ob-a-small">A</span>−</button>
            <button
              class="ob-font-btn"
              :disabled="fontLevel === fontSizes.length - 1"
              title="Increase text size"
              @click="changeFont(1)"
            >+<span class="ob-a-large">A</span></button>
          </div>
        </div>
        <p class="ob-restart-text">How would you like to proceed?</p>
        <div class="ob-restart-actions">
          <button class="btn btn-sm btn-primary w-100" @click="startTour(0)">
            ↺ &nbsp;Start from beginning
          </button>
          <button
            v-if="canResume"
            class="btn btn-sm btn-outline-info w-100 mt-2"
            @click="startTour(savedStep)"
          >▶ &nbsp;Resume: "{{ steps[savedStep].title }}"</button>
          <button class="btn btn-sm ob-dim-btn w-100 mt-2" @click="showRestartModal = false">
            Cancel
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import steps from '../config/onboardingSteps'

const STORAGE_KEY      = 'mps_onboarding_done'
const STORAGE_STEP_KEY = 'mps_onboarding_step'
const STORAGE_FONT_KEY = 'mps_onboarding_fontsize'
const TOOLTIP_W  = 300
const PADDING    = 8
const TOOLTIP_H  = 185   // approx height used for placement clamping

export default {
  name: 'OnboardingTour',
  data () {
    return {
      show: false,
      stepIndex: 0,
      steps,
      targetRect: null,
      tooltipStyle: {},
      // Font size: 3 levels — small / medium (default) / large
      fontSizes: ['0.85rem', '1rem', '1.15rem'],
      fontLevel: 1,
      // Restart modal
      showRestartModal: false,
      savedStep: null
    }
  },
  computed: {
    currentStep () {
      return this.steps[this.stepIndex]
    },
    panels () {
      if (!this.targetRect) return {}
      const { top, bottom, left, right } = this.targetRect
      const vw = window.innerWidth
      const vh = window.innerHeight
      const p  = PADDING
      return {
        top:    { top: '0px',               left: '0px',               width: vw + 'px',                        height: Math.max(0, top - p) + 'px' },
        bottom: { top: (bottom + p) + 'px', left: '0px',               width: vw + 'px',                        height: Math.max(0, vh - bottom - p) + 'px' },
        left:   { top: (top - p) + 'px',    left: '0px',               width: Math.max(0, left - p) + 'px',     height: (bottom - top + p * 2) + 'px' },
        right:  { top: (top - p) + 'px',    left: (right + p) + 'px',  width: Math.max(0, vw - right - p) + 'px', height: (bottom - top + p * 2) + 'px' }
      }
    },
    ringStyle () {
      if (!this.targetRect) return {}
      const { top, bottom, left, right } = this.targetRect
      const p = PADDING
      return {
        top:    (top    - p) + 'px',
        left:   (left   - p) + 'px',
        width:  (right  - left + p * 2) + 'px',
        height: (bottom - top  + p * 2) + 'px'
      }
    },
    canResume () {
      return this.savedStep !== null && this.savedStep > 0
    }
  },
  mounted () {
    // Restore saved font preference
    const savedFont = localStorage.getItem(STORAGE_FONT_KEY)
    if (savedFont !== null) this.fontLevel = parseInt(savedFont)

    // Listen for "Tutorial" button in NavBar
    this.$root.$on('restart-tour', this.openRestartModal)

    // Auto-start for first-time visitors
    if (!localStorage.getItem(STORAGE_KEY)) {
      this.show = true
      this.$nextTick(() => this.updatePosition())
    }
  },
  beforeDestroy () {
    this.$root.$off('restart-tour', this.openRestartModal)
  },
  methods: {
    // ── Font size ──────────────────────────────────────────────────────────────
    changeFont (delta) {
      const next = this.fontLevel + delta
      if (next >= 0 && next < this.fontSizes.length) {
        this.fontLevel = next
        localStorage.setItem(STORAGE_FONT_KEY, next)
      }
    },

    // ── Restart modal ──────────────────────────────────────────────────────────
    openRestartModal () {
      const saved = localStorage.getItem(STORAGE_STEP_KEY)
      this.savedStep = saved !== null ? parseInt(saved) : null
      this.showRestartModal = true
    },
    startTour (fromStep) {
      this.showRestartModal = false
      this.stepIndex = fromStep
      this.show = true
      this.$nextTick(() => this.updatePosition())
    },

    // ── Tour navigation ────────────────────────────────────────────────────────
    updatePosition () {
      const step = this.currentStep

      if (!step.target) {
        this.targetRect = null
        this.tooltipStyle = centeredStyle(TOOLTIP_W)
        return
      }

      // Resolve the active target + placement.
      // Try the primary target first; if it is hidden (zero rect) fall back to
      // step.mobile so the tour works on all screen sizes without any explicit
      // breakpoint checks — whichever element is actually visible wins.
      let target    = step.target
      let placement = step.placement || 'bottom'

      const primaryEl   = document.querySelector(target)
      const primaryRect = primaryEl ? primaryEl.getBoundingClientRect() : null
      const primaryHidden = !primaryRect || (primaryRect.width === 0 && primaryRect.height === 0)

      if (primaryHidden && step.mobile) {
        target    = step.mobile.target    !== undefined ? step.mobile.target    : target
        placement = step.mobile.placement !== undefined ? step.mobile.placement : placement
      }

      if (!target) {
        this.targetRect = null
        this.tooltipStyle = centeredStyle(TOOLTIP_W)
        return
      }

      const el   = document.querySelector(target)
      const rect = el ? el.getBoundingClientRect() : null

      // Final fallback to centered if resolved element is also missing/hidden
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        this.targetRect = null
        this.tooltipStyle = centeredStyle(TOOLTIP_W)
        return
      }

      // Store as plain object so Vue 2 reactivity tracks it
      this.targetRect = {
        top: rect.top, bottom: rect.bottom,
        left: rect.left, right: rect.right,
        width: rect.width, height: rect.height
      }

      const margin    = 14
      const vw        = window.innerWidth
      const vh        = window.innerHeight
      let top, left

      if (placement === 'bottom') {
        top  = rect.bottom + PADDING + margin
        left = rect.left + rect.width / 2 - TOOLTIP_W / 2
      } else if (placement === 'top') {
        top  = rect.top - PADDING - margin - TOOLTIP_H
        left = rect.left + rect.width / 2 - TOOLTIP_W / 2
      } else if (placement === 'right') {
        top  = rect.top + rect.height / 2 - TOOLTIP_H / 2
        left = rect.right + PADDING + margin
      } else if (placement === 'left') {
        top  = rect.top  + rect.height / 2 - TOOLTIP_H / 2
        left = rect.left - PADDING - margin - TOOLTIP_W
      }

      // Clamp to viewport
      left = Math.max(8, Math.min(left, vw - TOOLTIP_W - 8))
      top  = Math.max(8, Math.min(top,  vh - TOOLTIP_H  - 8))

      this.tooltipStyle = {
        position: 'fixed',
        top:  top  + 'px',
        left: left + 'px',
        width: TOOLTIP_W + 'px',
        zIndex: 10000
      }
    },
    next () {
      if (this.stepIndex < this.steps.length - 1) {
        this.stepIndex++
        this.$nextTick(() => this.updatePosition())
      } else {
        this.markDone()
      }
    },
    skip () {
      this.markDone()
    },
    markDone () {
      localStorage.setItem(STORAGE_KEY,      'true')
      localStorage.setItem(STORAGE_STEP_KEY, this.stepIndex)
      this.show = false
    }
  }
}

function centeredStyle (w) {
  return {
    position: 'fixed',
    top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: w + 'px',
    zIndex: 10000
  }
}
</script>

<style scoped>
/* ── Overlay panels ─────────────────────────────────────────────────────────── */
.ob-panel {
  position: fixed;
  background: rgba(0, 0, 0, 0.65);
  z-index: 9999;
  pointer-events: none;
}
.ob-overlay-solid {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 9999;
  pointer-events: none;
}

/* ── Spotlight ring ─────────────────────────────────────────────────────────── */
.ob-ring {
  position: fixed;
  border-radius: 4px;
  border: 2px solid rgba(54, 162, 235, 0.9);
  z-index: 9999;
  pointer-events: none;
  animation: ob-pulse 1.6s ease-in-out infinite;
}
@keyframes ob-pulse {
  0%,  100% { box-shadow: 0 0 0 3px rgba(54, 162, 235, 0.25); }
  50%        { box-shadow: 0 0 0 7px rgba(54, 162, 235, 0.08); }
}

/* ── Shared header row (step counter + font controls) ───────────────────────── */
.ob-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.ob-step-counter,
.ob-restart-label {
  font-size: 0.72em;
  color: #666;
}
.ob-restart-label {
  font-weight: 600;
  color: #36a2eb;
  font-size: 0.9em;
}

/* ── Font-size controls ─────────────────────────────────────────────────────── */
.ob-font-ctrl {
  display: flex;
  gap: 2px;
}
.ob-font-btn {
  background: transparent;
  border: 1px solid #3a3f46;
  border-radius: 3px;
  color: #777;
  cursor: pointer;
  padding: 1px 5px;
  font-size: 0.7em;
  line-height: 1.4;
  transition: color 0.15s, border-color 0.15s;
}
.ob-font-btn:hover:not(:disabled) {
  color: #aaa;
  border-color: #555;
}
.ob-font-btn:disabled {
  opacity: 0.3;
  cursor: default;
}
.ob-a-small { font-size: 0.75em; }
.ob-a-large { font-size: 1.25em; }

/* ── Tooltip bubble ─────────────────────────────────────────────────────────── */
.ob-tooltip {
  position: fixed;
  background: #1e2328;
  border: 1px solid #3a3f46;
  border-radius: 6px;
  padding: 14px 16px;
  color: #e8e8e8;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.7);
}
.ob-title {
  color: #36a2eb;
  margin-bottom: 6px;
  font-size: 1.05em;
  font-weight: 600;
}
.ob-text {
  font-size: 0.92em;
  margin-bottom: 12px;
  line-height: 1.55;
  color: #aaa;
}
.ob-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* ── Restart modal ──────────────────────────────────────────────────────────── */
.ob-restart-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 10001;
}
.ob-restart-card {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  background: #1e2328;
  border: 1px solid #3a3f46;
  border-radius: 6px;
  padding: 16px 18px;
  color: #e8e8e8;
  box-shadow: 0 6px 32px rgba(0, 0, 0, 0.8);
  z-index: 10002;
}
.ob-restart-text {
  font-size: 0.9em;
  color: #aaa;
  margin-bottom: 14px;
}
.ob-restart-actions {
  display: flex;
  flex-direction: column;
}

/* ── Shared muted button ────────────────────────────────────────────────────── */
.ob-dim-btn {
  color: #555 !important;
  border: 1px solid #444 !important;
  background: transparent !important;
}
.ob-dim-btn:hover {
  color: #888 !important;
  border-color: #666 !important;
}
</style>
